import { gql } from "@apollo/client";
import { useRouter } from "next/router";
import { FC } from "react";

import SelectUsersDialog, {
  USER_SELECTION_FILTERS,
} from "@src/features/contentLibrary/assignmentDialogs/SelectUsersDialog";
import { useToast } from "@src/lib/hooks/useToast";
import { GraphqlOperations, UserSelectionInput } from "@src/types.generated";

import { useCreateDirectMessageMutation } from "./CreateDirectMessageDialog.generated";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

const PEOPLE_ONLY_HIDE_FILTERS = USER_SELECTION_FILTERS.filter(
  (filter) => filter !== "people",
);

export const useCreateDirectMessage = (
  onOpenChange: (open: boolean) => void,
): ReturnType<typeof useCreateDirectMessageMutation> => {
  const router = useRouter();
  const { addErrorToast } = useToast();

  return useCreateDirectMessageMutation({
    refetchQueries: [GraphqlOperations.Query.NewMessagingChatSections],
    onError: (error) => {
      addErrorToast({ ...error, callsite: "create_direct_message" });
      onOpenChange(false);
    },
    onCompleted: (data) => {
      if (!data.createDirectMessage.success || !data.createDirectMessage.chat) {
        addErrorToast({ data, callsite: "create_direct_message" });
        onOpenChange(false);
        return;
      }
      onOpenChange(false);
      router.push({
        pathname: "/messages/my-chats/[chatId]",
        query: { chatId: data.createDirectMessage.chat.id },
      });
    },
  });
};

const CreateDirectMessageDialog: FC<Props> = ({ open, onOpenChange }) => {
  const [createDirectMessage] = useCreateDirectMessage(onOpenChange);

  const handleSelect = (selected: UserSelectionInput) => {
    const userIds = selected.userIds ?? [];
    if (userIds.length === 0) return;
    createDirectMessage({ variables: { input: { userIds } } });
  };

  return (
    <SelectUsersDialog
      open={open}
      onOpenChange={onOpenChange}
      title="New direct message"
      pillColor="purple"
      requireSelection
      defaultFilter="people"
      hideFilters={PEOPLE_ONLY_HIDE_FILTERS}
      onSelect={handleSelect}
    />
  );
};

gql`
  mutation CreateDirectMessage($input: CreateDirectMessageInput!) {
    createDirectMessage(input: $input) {
      success
      error {
        code
      }
      chat {
        ...ChatListItem
      }
    }
  }
`;

export default CreateDirectMessageDialog;
